import { useState, useEffect, useCallback, useMemo } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, Plus, Trash, DownloadSimple, FileText, Robot, Warning, FloppyDisk, ChartBar, ArrowLeft, PlusCircle, FunnelSimple, X, GitDiff } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { log } from "@/lib/logger";

const EMPTY_FIELD = { name: "", description: "", type: "string" };
const newFieldId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `f_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

// Mirror of backend `_analysis_stats` for client-side recomputation under filters.
function computeStats(rows, fields) {
  const out = {};
  const round = (x, n = 3) => Math.round(x * 10 ** n) / 10 ** n;
  for (const f of fields) {
    const name = f.name;
    const ftype = f.type || "string";
    const values = [];
    for (const r of rows) {
      if (r.error) continue;
      const v = r.extracted?.[name];
      if (v === null || v === undefined || v === "") continue;
      values.push(v);
    }
    if (values.length === 0) { out[name] = { type: ftype, count: 0 }; continue; }
    if (ftype === "number") {
      const nums = values.map(Number).filter((n) => !Number.isNaN(n));
      if (nums.length === 0) { out[name] = { type: "number", count: 0 }; continue; }
      const lo = Math.min(...nums), hi = Math.max(...nums);
      const binCount = nums.length >= 6 ? 10 : Math.max(3, nums.length);
      const width = hi > lo ? (hi - lo) / binCount : 1;
      const hist = [];
      for (let i = 0; i < binCount; i++) {
        const bLo = lo + i * width, bHi = lo + (i + 1) * width;
        const c = i === binCount - 1
          ? nums.filter((x) => x >= bLo && x <= bHi).length
          : nums.filter((x) => x >= bLo && x < bHi).length;
        hist.push({ label: `${round(bLo, 2)}–${round(bHi, 2)}`, count: c });
      }
      const sorted = [...nums].sort((a, b) => a - b);
      const median = sorted.length % 2
        ? sorted[Math.floor(sorted.length / 2)]
        : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
      out[name] = {
        type: "number",
        count: nums.length,
        mean: round(nums.reduce((a, b) => a + b, 0) / nums.length, 3),
        median: round(median, 3),
        min: lo,
        max: hi,
        histogram: hist,
      };
    } else {
      const counts = new Map();
      for (const v of values) {
        const k = String(v).slice(0, 120);
        counts.set(k, (counts.get(k) || 0) + 1);
      }
      const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8).map(([value, count]) => ({ value, count }));
      out[name] = { type: ftype, count: values.length, top };
    }
  }
  return out;
}

function rowMatches(row, filters, fields) {
  if (row.error) return false;
  for (const f of fields) {
    const flt = filters[f.name];
    if (!flt) continue;
    const val = row.extracted?.[f.name];
    if (f.type === "number") {
      const n = Number(val);
      if (Number.isNaN(n)) return false;
      if (flt.min !== "" && flt.min !== undefined && flt.min !== null && n < Number(flt.min)) return false;
      if (flt.max !== "" && flt.max !== undefined && flt.max !== null && n > Number(flt.max)) return false;
    } else {
      if (flt.value && String(val) !== flt.value) return false;
    }
  }
  return true;
}

function isFilterActive(f) {
  return !!(f && (f.value || (f.min !== undefined && f.min !== "") || (f.max !== undefined && f.max !== "")));
}

// Grouped histogram: shared bins on the union of A+B numeric values.
function computeCompareHistogram(fieldName, rowsA, rowsB) {
  const round = (x, n = 2) => Math.round(x * 10 ** n) / 10 ** n;
  const numsA = rowsA.map((r) => Number(r.extracted?.[fieldName])).filter((n) => !Number.isNaN(n));
  const numsB = rowsB.map((r) => Number(r.extracted?.[fieldName])).filter((n) => !Number.isNaN(n));
  const all = [...numsA, ...numsB];
  if (all.length === 0) return [];
  const lo = Math.min(...all), hi = Math.max(...all);
  const binCount = all.length >= 6 ? 10 : Math.max(3, all.length);
  const width = hi > lo ? (hi - lo) / binCount : 1;
  const bins = [];
  for (let i = 0; i < binCount; i++) {
    const bLo = lo + i * width, bHi = lo + (i + 1) * width;
    const inRange = (arr) => arr.filter((x) => (i === binCount - 1 ? (x >= bLo && x <= bHi) : (x >= bLo && x < bHi))).length;
    bins.push({ label: `${round(bLo, 2)}–${round(bHi, 2)}`, A: inRange(numsA), B: inRange(numsB) });
  }
  return bins;
}

export default function DocumentAnalyzer() {
  const [view, setView] = useState("analyze"); // analyze | saved | detail
  const [detailId, setDetailId] = useState(null);
  const [saved, setSaved] = useState([]);
  const [fields, setFields] = useState([{ ...EMPTY_FIELD, __id: newFieldId(), name: "age", description: "Âge du patient en années", type: "number" }]);
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveName, setSaveName] = useState("");

  const addField = () => setFields([...fields, { ...EMPTY_FIELD, __id: newFieldId() }]);
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const updateField = (i, patch) => setFields(fields.map((f, idx) => idx === i ? { ...f, ...patch } : f));

  const onFiles = (e) => {
    const f = Array.from(e.target.files || []);
    const valid = f.filter((x) => /\.(pdf|docx|txt)$/i.test(x.name));
    if (valid.length < f.length) toast.warning("Seuls PDF, DOCX et TXT sont acceptés");
    // Merge with existing, dedupe by name+size, cap at 20
    setFiles((prev) => {
      const key = (x) => `${x.name}:${x.size}`;
      const seen = new Set(prev.map(key));
      const merged = [...prev];
      for (const nf of valid) {
        if (!seen.has(key(nf))) { merged.push(nf); seen.add(key(nf)); }
      }
      if (merged.length > 20) toast.warning("Maximum 20 fichiers — les surplus ont été ignorés");
      return merged.slice(0, 20);
    });
    e.target.value = ""; // allow re-selecting the same file after removal
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  const clearFiles = () => setFiles([]);

  const loadSaved = useCallback(async () => {
    try {
      const res = await fetch(`${API}/studies/analyses`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSaved(data.items || []);
    } catch (err) {
      log.error("list analyses", err);
      toast.error("Impossible de charger les analyses");
    }
    // All references (`API`, `log`, `toast`, setSaved) are stable — module
    // imports and React setState refs never change identity. `[]` is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (view === "saved") loadSaved(); }, [view, loadSaved]);

  const analyze = async () => {
    if (fields.some((f) => !f.name.trim())) return toast.error("Tous les champs doivent avoir un nom");
    if (files.length === 0) return toast.error("Ajoutez au moins un document");
    setBusy(true);
    setResults(null);
    try {
      const fd = new FormData();
      fd.append("fields", JSON.stringify(fields));
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/studies/analyze-documents`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResults(data);
      setSaveName(`Extraction ${new Date().toLocaleDateString("fr-FR")}`);
      toast.success(`${data.results.length} document(s) analysé(s)`);
    } catch (err) {
      log.error("analyze docs", err);
      toast.error(err.message || "Analyse impossible");
    } finally { setBusy(false); }
  };

  const saveExport = async () => {
    if (!results || !saveName.trim()) return toast.error("Nom requis");
    try {
      const res = await fetch(`${API}/studies/analyses`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: saveName.trim(),
          fields,
          rows: results.results,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Erreur");
      toast.success("Extraction enregistrée");
      setResults(null);
      setFiles([]);
      setView("saved");
    } catch (err) {
      log.error("save analysis", err);
      toast.error(err.message || "Enregistrement impossible");
    }
  };

  const exportCsvFrom = (rows, cols) => {
    const header = ["fichier", ...cols];
    const lines = [header.join(",")];
    for (const r of rows) {
      const row = [`"${(r.filename || "").replace(/"/g, '""')}"`];
      for (const f of cols) {
        const v = r.extracted ? r.extracted[f] : (r.error ? `ERREUR: ${r.error}` : "");
        row.push(`"${(v == null ? "" : String(v)).replace(/"/g, '""')}"`);
      }
      lines.push(row.join(","));
    }
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `extraction-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => results && exportCsvFrom(results.results, results.fields);

  if (view === "detail") {
    return <SavedDetail id={detailId} onBack={() => { setView("saved"); setDetailId(null); }} onExport={exportCsvFrom} onDeleted={() => { setView("saved"); setDetailId(null); loadSaved(); }} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1 bg-white border border-[#E1E9F0] rounded-[14px] p-1" role="tablist">
          <button
            onClick={() => setView("analyze")}
            className={`px-4 py-2 rounded-[10px] text-[14px] font-semibold inline-flex items-center gap-2 transition-colors ${view === "analyze" ? "bg-[#5F758F] text-white" : "text-[#5F758F] hover:text-[#1E3A5F]"}`}
            data-testid="doc-tab-analyze"
          >Nouvelle analyse</button>
          <button
            onClick={() => setView("saved")}
            className={`px-4 py-2 rounded-[10px] text-[14px] font-semibold inline-flex items-center gap-2 transition-colors ${view === "saved" ? "bg-[#5F758F] text-white" : "text-[#5F758F] hover:text-[#1E3A5F]"}`}
            data-testid="doc-tab-saved"
          >Extractions enregistrées</button>
        </div>
      </div>

      {view === "saved" && (
        <SavedList items={saved} onOpen={(id) => { setDetailId(id); setView("detail"); }} onReload={loadSaved} />
      )}

      {view === "analyze" && <>
      <div className="bg-[#E6F1F5] border border-[#3A8FA5]/30 rounded-lg p-4 text-sm text-[#1E3A5F] flex gap-3">
        <Robot size={20} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
        <div>
          <strong>Extraction IA de compte-rendus médicaux.</strong> Uploadez vos CR (PDF, DOCX, TXT — max 20, 10 Mo chacun),
          définissez les champs à extraire, l'IA Claude Sonnet 5 remplit un tableau exportable en CSV, que vous pouvez enregistrer pour vos statistiques ultérieures.
          <em className="block mt-1 text-[#3A8FA5]">
            Les documents ne sont jamais stockés — et toutes les données personnelles (nom, prénom, date de naissance,
            adresse, téléphone, NIR, email) sont <strong>anonymisées automatiquement avant l'envoi à l'IA</strong> (conforme RGPD/HDS).
          </em>
        </div>
      </div>

      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5">
        <h3 className="font-serif text-xl mb-4">1. Définir les champs à extraire</h3>
        <div className="space-y-2">
          {fields.map((f, i) => (
            <div key={f.__id || i} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-3">
                {i === 0 && <Label className="text-xs tracking-widest uppercase text-[#5F758F] mb-1 block">Nom du champ</Label>}
                <Input value={f.name} onChange={(e) => updateField(i, { name: e.target.value })} placeholder="âge, PSA, diagnostic…" className="bg-white border-[#E1E9F0]" />
              </div>
              <div className="col-span-6">
                {i === 0 && <Label className="text-xs tracking-widest uppercase text-[#5F758F] mb-1 block">Description pour l'IA</Label>}
                <Input value={f.description} onChange={(e) => updateField(i, { description: e.target.value })} placeholder="Ex: Âge du patient en années au moment du diagnostic" className="bg-white border-[#E1E9F0]" />
              </div>
              <div className="col-span-2">
                {i === 0 && <Label className="text-xs tracking-widest uppercase text-[#5F758F] mb-1 block">Type</Label>}
                <Select value={f.type} onValueChange={(v) => updateField(i, { type: v })}>
                  <SelectTrigger className="bg-white border-[#E1E9F0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">Texte</SelectItem>
                    <SelectItem value="number">Nombre</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="boolean">Oui/Non</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1">
                <Button variant="ghost" size="sm" onClick={() => removeField(i)} disabled={fields.length === 1} className="text-red-600 w-full">
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={addField} className="mt-3 rounded-md" data-testid="doc-add-field">
          <Plus size={14} className="mr-1.5" /> Ajouter un champ
        </Button>
      </div>

      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5">
        <h3 className="font-serif text-xl mb-4">2. Uploader les compte-rendus</h3>
        <label className="border-2 border-dashed border-[#E1E9F0] rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-[#3A8FA5] hover:bg-[#F4F7FB] transition-colors">
          <UploadSimple size={32} weight="duotone" className="text-[#3A8FA5] mb-2" />
          <p className="text-sm font-medium text-[#1E3A5F]">Ajouter des fichiers</p>
          <p className="text-xs text-[#5F758F] mt-1">PDF, DOCX, TXT — sélectionnez plusieurs fois pour ajouter au fur et à mesure (20 max)</p>
          <input type="file" multiple accept=".pdf,.docx,.txt" className="hidden" onChange={onFiles} data-testid="doc-analyze-files" />
        </label>
        {files.length > 0 && (
          <>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs tracking-widest uppercase text-[#5F758F]">{files.length} fichier(s) — max 20</p>
              <Button variant="ghost" size="sm" onClick={clearFiles} className="text-[#5F758F] hover:text-red-600" data-testid="doc-clear-files">
                <Trash size={12} className="mr-1" /> Tout retirer
              </Button>
            </div>
            <ul className="mt-2 space-y-1">
              {files.map((f, i) => (
                <li key={`${f.name}-${f.size}-${i}`} className="text-sm text-[#1E3A5F] flex items-center gap-2 group">
                  <FileText size={14} className="text-[#3A8FA5]" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-[#5F758F]">{(f.size / 1024).toFixed(0)} Ko</span>
                  <button onClick={() => removeFile(i)} className="opacity-40 group-hover:opacity-100 text-red-600 transition-opacity" aria-label="Retirer">
                    <Trash size={12} />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button onClick={analyze} disabled={busy || files.length === 0} className="rounded-md bg-[#3A8FA5] hover:bg-[#465F51] text-white" data-testid="doc-analyze-btn">
          <Robot size={16} className="mr-2" /> {busy ? "Analyse en cours…" : "Lancer l'analyse"}
        </Button>
      </div>

      {results && (
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5">
          <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
            <h3 className="font-serif text-xl">3. Résultats</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="Nom de l'étude"
                className="w-56 bg-white border-[#E1E9F0]"
                data-testid="doc-save-name"
              />
              <Button size="sm" onClick={saveExport} className="rounded-md bg-[#3A8FA5] hover:bg-[#465F51] text-white" data-testid="doc-save-btn">
                <FloppyDisk size={14} className="mr-1.5" /> Enregistrer
              </Button>
              <Button size="sm" variant="outline" onClick={exportCsv} className="rounded-md" data-testid="doc-export-csv">
                <DownloadSimple size={14} className="mr-1.5" /> CSV
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs tracking-widest uppercase text-[#5F758F] border-b border-[#E1E9F0]">
                  <th className="py-2 pr-3">Fichier</th>
                  {results.fields.map((f) => <th key={f} className="py-2 pr-3">{f}</th>)}
                </tr>
              </thead>
              <tbody>
                {results.results.map((r, i) => (
                  <tr key={`${r.filename}-${i}`} className="border-b border-[#E1E9F0]">
                    <td className="py-2 pr-3 text-[#1E3A5F] font-medium truncate max-w-xs">{r.filename}</td>
                    {r.error ? (
                      <td colSpan={results.fields.length} className="py-2 pr-3 text-[#B9502C] text-xs">
                        <Warning size={12} className="inline mr-1" />{r.error}
                      </td>
                    ) : (
                      results.fields.map((f) => (
                        <td key={f} className="py-2 pr-3 text-[#1E3A5F]">
                          {r.extracted?.[f] == null ? <span className="text-[#5F758F] italic">—</span> : String(r.extracted[f])}
                        </td>
                      ))
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}

function SavedList({ items, onOpen, onReload }) {
  const del = async (id, e) => {
    e.stopPropagation();
    if (!confirm("Supprimer cette extraction ?")) return;
    try {
      const res = await fetch(`${API}/studies/analyses/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Erreur");
      toast.success("Supprimé");
      onReload();
    } catch (err) {
      log.error("delete analysis", err);
      toast.error("Suppression impossible");
    }
  };

  if (items.length === 0) {
    return (
      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-10 text-center">
        <ChartBar size={40} weight="duotone" className="text-[#3A8FA5] mx-auto mb-3" />
        <p className="text-[#1E3A5F] font-medium">Aucune extraction enregistrée</p>
        <p className="text-sm text-[#5F758F] mt-1">Lancez une analyse puis cliquez sur « Enregistrer » pour la retrouver ici.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E1E9F0] rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs tracking-widest uppercase text-[#5F758F] border-b border-[#E1E9F0] bg-[#F4F7FB]">
            <th className="py-3 px-4">Étude</th>
            <th className="py-3 px-4">Documents</th>
            <th className="py-3 px-4">Champs</th>
            <th className="py-3 px-4">Date</th>
            <th className="py-3 px-4"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.analysis_id} onClick={() => onOpen(it.analysis_id)} className="border-b border-[#E1E9F0] cursor-pointer hover:bg-[#F4F7FB] transition-colors" data-testid={`saved-row-${it.analysis_id}`}>
              <td className="py-3 px-4 text-[#1E3A5F] font-medium">{it.name}</td>
              <td className="py-3 px-4 text-[#5F758F]">{it.documents}</td>
              <td className="py-3 px-4 text-[#5F758F]">{it.fields.join(", ")}</td>
              <td className="py-3 px-4 text-[#5F758F]">{new Date(it.created_at).toLocaleDateString("fr-FR")}</td>
              <td className="py-3 px-4 text-right">
                <Button size="sm" variant="ghost" onClick={(e) => del(it.analysis_id, e)} className="text-red-600 hover:bg-red-50">
                  <Trash size={14} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Reusable filter grid (used in both single-filter and compare modes).
function FilterGrid({ fields, filters, setFilter, baseStats, distinctValues, compact = false, testidPrefix = "filter" }) {
  const gridCls = compact
    ? "grid grid-cols-1 gap-2"
    : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3";
  return (
    <div className={gridCls}>
      {fields.map((f) => {
        const flt = filters[f.name] || {};
        const stat = baseStats[f.name] || {};
        if (f.type === "number") {
          return (
            <div key={f.name} className="border border-[#E1E9F0] rounded-md p-3 bg-white">
              <div className="text-xs font-medium text-[#1E3A5F] mb-2">{f.name}
                {stat.min !== undefined && <span className="text-[#5F758F] ml-2 font-normal">({stat.min} – {stat.max})</span>}
              </div>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  value={flt.min ?? ""}
                  onChange={(e) => setFilter(f.name, { min: e.target.value })}
                  placeholder="min"
                  className="h-8 text-sm bg-white border-[#E1E9F0]"
                  data-testid={`${testidPrefix}-min-${f.name}`}
                />
                <span className="text-[#5F758F] text-xs">–</span>
                <Input
                  type="number"
                  value={flt.max ?? ""}
                  onChange={(e) => setFilter(f.name, { max: e.target.value })}
                  placeholder="max"
                  className="h-8 text-sm bg-white border-[#E1E9F0]"
                  data-testid={`${testidPrefix}-max-${f.name}`}
                />
              </div>
            </div>
          );
        }
        const opts = distinctValues[f.name] || [];
        return (
          <div key={f.name} className="border border-[#E1E9F0] rounded-md p-3 bg-white">
            <div className="text-xs font-medium text-[#1E3A5F] mb-2">{f.name}
              <span className="text-[#5F758F] ml-2 font-normal">({opts.length} valeur{opts.length > 1 ? "s" : ""})</span>
            </div>
            <Select value={flt.value || "__all__"} onValueChange={(v) => setFilter(f.name, { value: v === "__all__" ? "" : v })}>
              <SelectTrigger className="h-8 text-sm bg-white border-[#E1E9F0]" data-testid={`${testidPrefix}-select-${f.name}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toutes les valeurs</SelectItem>
                {opts.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </div>
  );
}

function SavedDetail({ id, onBack, onExport, onDeleted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appendFiles, setAppendFiles] = useState([]);
  const [appending, setAppending] = useState(false);
  const [filters, setFilters] = useState({}); // { [fieldName]: {min,max} | {value} }
  const [compareMode, setCompareMode] = useState(false);
  const [filtersB, setFiltersB] = useState({});

  const setFilter = (name, patch) => setFilters((prev) => ({ ...prev, [name]: { ...(prev[name] || {}), ...patch } }));
  const setFilterB = (name, patch) => setFiltersB((prev) => ({ ...prev, [name]: { ...(prev[name] || {}), ...patch } }));
  const resetFilters = () => setFilters({});
  const resetFiltersB = () => setFiltersB({});
  const activeFilterCount = Object.values(filters).filter(isFilterActive).length;
  const activeFilterCountB = Object.values(filtersB).filter(isFilterActive).length;

  const reload = useCallback(async () => {
    try {
      const res = await fetch(`${API}/studies/analyses/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      log.error("get analysis", err);
      toast.error("Chargement impossible");
    } finally { setLoading(false); }
    // `id` is the only dynamic dependency; the rest (`API`, `log`, `toast`,
    // setState refs) are stable module/React references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  const onAppendFiles = (e) => {
    const f = Array.from(e.target.files || []);
    const valid = f.filter((x) => /\.(pdf|docx|txt)$/i.test(x.name));
    if (valid.length < f.length) toast.warning("Seuls PDF, DOCX et TXT sont acceptés");
    setAppendFiles((prev) => {
      const key = (x) => `${x.name}:${x.size}`;
      const seen = new Set(prev.map(key));
      const merged = [...prev];
      for (const nf of valid) if (!seen.has(key(nf))) merged.push(nf);
      if (merged.length > 20) toast.warning("Maximum 20 fichiers par ajout");
      return merged.slice(0, 20);
    });
    e.target.value = "";
  };

  const appendNow = async () => {
    if (appendFiles.length === 0) return;
    setAppending(true);
    try {
      const fd = new FormData();
      appendFiles.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/studies/analyses/${id}/append`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const out = await res.json();
      toast.success(`${out.added} CR ajouté(s)`);
      setAppendFiles([]);
      await reload();
    } catch (err) {
      log.error("append", err);
      toast.error(err.message || "Ajout impossible");
    } finally { setAppending(false); }
  };

  const del = async () => {
    if (!confirm("Supprimer cette extraction ?")) return;
    try {
      const res = await fetch(`${API}/studies/analyses/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Erreur");
      toast.success("Supprimé");
      onDeleted();
    } catch (err) {
      log.error("delete analysis", err);
      toast.error("Suppression impossible");
    }
  };

  const filteredRows = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => rowMatches(r, filters, data.fields));
  }, [data, filters]);

  const filteredRowsB = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => rowMatches(r, filtersB, data.fields));
  }, [data, filtersB]);

  const displayStats = useMemo(() => {
    if (!data) return {};
    return activeFilterCount > 0 ? computeStats(filteredRows, data.fields) : (data.stats || {});
  }, [data, filteredRows, activeFilterCount]);

  const displayStatsB = useMemo(() => {
    if (!data) return {};
    return computeStats(filteredRowsB, data.fields);
  }, [data, filteredRowsB]);

  // Distinct values per non-numeric field, for filter dropdowns
  const distinctValues = useMemo(() => {
    if (!data) return {};
    const map = {};
    for (const f of data.fields) {
      if (f.type === "number") continue;
      const s = new Set();
      for (const r of data.rows) {
        if (r.error) continue;
        const v = r.extracted?.[f.name];
        if (v !== null && v !== undefined && v !== "") s.add(String(v));
      }
      map[f.name] = [...s].sort();
    }
    return map;
  }, [data]);

  if (loading) return <div className="text-sm text-[#5F758F]">Chargement…</div>;
  if (!data) return null;
  const fieldNames = data.fields.map((f) => f.name);
  const rowsToShow = activeFilterCount > 0 ? filteredRows : data.rows;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-[#5F758F]">
            <ArrowLeft size={16} className="mr-1" /> Retour
          </Button>
          <div>
            <h2 className="font-serif text-2xl text-[#1E3A5F]">{data.name}</h2>
            <p className="text-xs text-[#5F758F]">
              {data.rows.length} document(s) · Enregistré le {new Date(data.created_at).toLocaleDateString("fr-FR")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onExport(data.rows, fieldNames)} className="rounded-md">
            <DownloadSimple size={14} className="mr-1.5" /> CSV
          </Button>
          <Button size="sm" variant="ghost" onClick={del} className="text-red-600 hover:bg-red-50">
            <Trash size={14} className="mr-1.5" /> Supprimer
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="font-serif text-xl flex items-center gap-2">
            <PlusCircle size={20} weight="duotone" className="text-[#3A8FA5]" /> Agrandir la cohorte
          </h3>
          <p className="text-xs text-[#5F758F]">Les CR ajoutés sont analysés avec les mêmes champs.</p>
        </div>
        <label className="border-2 border-dashed border-[#E1E9F0] rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-[#3A8FA5] hover:bg-[#F4F7FB] transition-colors">
          <UploadSimple size={24} weight="duotone" className="text-[#3A8FA5] mb-1" />
          <p className="text-sm font-medium text-[#1E3A5F]">Ajouter des CR</p>
          <p className="text-xs text-[#5F758F] mt-1">PDF, DOCX, TXT — 20 max par ajout</p>
          <input type="file" multiple accept=".pdf,.docx,.txt" className="hidden" onChange={onAppendFiles} data-testid="doc-append-files" />
        </label>
        {appendFiles.length > 0 && (
          <>
            <ul className="mt-3 space-y-1">
              {appendFiles.map((f, i) => (
                <li key={`${f.name}-${i}`} className="text-sm text-[#1E3A5F] flex items-center gap-2">
                  <FileText size={14} className="text-[#3A8FA5]" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-[#5F758F]">{(f.size / 1024).toFixed(0)} Ko</span>
                  <button onClick={() => setAppendFiles((p) => p.filter((_, idx) => idx !== i))} className="text-red-600">
                    <Trash size={12} />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex justify-end mt-3">
              <Button size="sm" onClick={appendNow} disabled={appending} className="rounded-md bg-[#3A8FA5] hover:bg-[#465F51] text-white" data-testid="doc-append-btn">
                <Robot size={14} className="mr-1.5" /> {appending ? "Analyse en cours…" : `Analyser et ajouter (${appendFiles.length})`}
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="font-serif text-xl flex items-center gap-2">
            <FunnelSimple size={20} weight="duotone" className="text-[#3A8FA5]" />
            {compareMode ? "Filtres — comparaison A vs B" : "Filtres sous-groupe"}
          </h3>
          <div className="flex items-center gap-2 text-xs">
            <Button
              size="sm"
              variant={compareMode ? "default" : "outline"}
              onClick={() => setCompareMode((v) => !v)}
              className={`h-7 px-3 rounded-md ${compareMode ? "bg-[#3A8FA5] hover:bg-[#465F51] text-white" : ""}`}
              data-testid="compare-toggle"
            >
              <GitDiff size={12} className="mr-1" /> {compareMode ? "Quitter la comparaison" : "Comparer 2 cohortes"}
            </Button>
          </div>
        </div>

        {!compareMode && (
          <>
            <div className="flex items-center gap-3 text-xs mb-3">
              <span className="text-[#5F758F]">
                <strong className="text-[#1E3A5F]">{filteredRows.length}</strong> CR sur {data.rows.filter((r) => !r.error).length}
              </span>
              {activeFilterCount > 0 && (
                <Button size="sm" variant="ghost" onClick={resetFilters} className="text-[#3A8FA5] hover:text-red-600 h-7 px-2" data-testid="filters-reset">
                  <X size={12} className="mr-1" /> Réinitialiser
                </Button>
              )}
            </div>
            <FilterGrid fields={data.fields} filters={filters} setFilter={setFilter} baseStats={data.stats || {}} distinctValues={distinctValues} testidPrefix="filter" />
          </>
        )}

        {compareMode && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border-2 border-[#3A8FA5]/40 rounded-md p-3 bg-[#F9FBFA]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#3A8FA5] text-white flex items-center justify-center text-xs font-bold">A</span>
                  <span className="text-sm text-[#1E3A5F]"><strong>{filteredRows.length}</strong> CR</span>
                </div>
                {activeFilterCount > 0 && (
                  <Button size="sm" variant="ghost" onClick={resetFilters} className="text-[#3A8FA5] h-7 px-2" data-testid="filters-reset-a">
                    <X size={12} className="mr-1" /> Réinitialiser
                  </Button>
                )}
              </div>
              <FilterGrid fields={data.fields} filters={filters} setFilter={setFilter} baseStats={data.stats || {}} distinctValues={distinctValues} compact testidPrefix="filter-a" />
            </div>
            <div className="border-2 border-[#B9502C]/40 rounded-md p-3 bg-[#FEF7F3]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#B9502C] text-white flex items-center justify-center text-xs font-bold">B</span>
                  <span className="text-sm text-[#1E3A5F]"><strong>{filteredRowsB.length}</strong> CR</span>
                </div>
                {activeFilterCountB > 0 && (
                  <Button size="sm" variant="ghost" onClick={resetFiltersB} className="text-[#B9502C] h-7 px-2" data-testid="filters-reset-b">
                    <X size={12} className="mr-1" /> Réinitialiser
                  </Button>
                )}
              </div>
              <FilterGrid fields={data.fields} filters={filtersB} setFilter={setFilterB} baseStats={data.stats || {}} distinctValues={distinctValues} compact testidPrefix="filter-b" />
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5">
        <h3 className="font-serif text-xl mb-4 flex items-center gap-2">
          <ChartBar size={20} weight="duotone" className="text-[#3A8FA5]" />
          Statistiques
          {compareMode && <span className="text-xs font-normal text-[#5F758F] ml-1">(A vs B)</span>}
          {!compareMode && activeFilterCount > 0 && <span className="text-xs font-normal text-[#3A8FA5] ml-1">(filtrées)</span>}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.fields.map((f) => {
            if (compareMode) {
              const sA = displayStats[f.name] || { count: 0 };
              const sB = displayStatsB[f.name] || { count: 0 };
              return (
                <div key={f.name} className="border border-[#E1E9F0] rounded-md p-4 bg-[#F4F7FB]/40">
                  <div className="text-xs tracking-widest uppercase text-[#5F758F]">{f.type}</div>
                  <div className="font-medium text-[#1E3A5F] mb-3">{f.name}</div>
                  {f.type === "number" ? (
                    <>
                      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                        <div className="border-l-4 border-[#3A8FA5] pl-2">
                          <div className="text-[10px] tracking-widest uppercase text-[#3A8FA5] font-bold">A</div>
                          {sA.count > 0 ? (
                            <>
                              <div>n {sA.count} · <span className="text-[#5F758F]">moy</span> {sA.mean}</div>
                              <div className="text-[#5F758F] text-xs">min-max {sA.min} – {sA.max}</div>
                            </>
                          ) : <div className="text-[#5F758F] italic text-xs">Aucune valeur</div>}
                        </div>
                        <div className="border-l-4 border-[#B9502C] pl-2">
                          <div className="text-[10px] tracking-widest uppercase text-[#B9502C] font-bold">B</div>
                          {sB.count > 0 ? (
                            <>
                              <div>n {sB.count} · <span className="text-[#5F758F]">moy</span> {sB.mean}</div>
                              <div className="text-[#5F758F] text-xs">min-max {sB.min} – {sB.max}</div>
                            </>
                          ) : <div className="text-[#5F758F] italic text-xs">Aucune valeur</div>}
                        </div>
                      </div>
                      {(sA.count > 0 || sB.count > 0) && (() => {
                        const bins = computeCompareHistogram(f.name, filteredRows, filteredRowsB);
                        return bins.length > 0 && (
                          <div style={{ width: "100%", height: 200 }}>
                            <ResponsiveContainer>
                              <BarChart data={bins} margin={{ top: 4, right: 8, left: -20, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E1E9F0" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#5F758F" }} interval={0} angle={-25} textAnchor="end" height={50} />
                                <YAxis tick={{ fontSize: 10, fill: "#5F758F" }} allowDecimals={false} />
                                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E1E9F0", borderRadius: 6, fontSize: 12 }} />
                                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                                <Bar dataKey="A" name="Cohorte A" fill="#3A8FA5" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="B" name="Cohorte B" fill="#B9502C" radius={[3, 3, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <div className="text-sm">
                      {(() => {
                        // Merge top values from A and B to show side-by-side counts
                        const mapA = new Map((sA.top || []).map((t) => [t.value, t.count]));
                        const mapB = new Map((sB.top || []).map((t) => [t.value, t.count]));
                        const allVals = [...new Set([...mapA.keys(), ...mapB.keys()])];
                        if (allVals.length === 0) return <div className="text-[#5F758F] italic">Aucune valeur</div>;
                        return (
                          <ul className="space-y-1">
                            <li className="grid grid-cols-[1fr_auto_auto] gap-3 text-[10px] tracking-widest uppercase text-[#5F758F]">
                              <span>Valeur</span><span className="text-[#3A8FA5] font-bold">A</span><span className="text-[#B9502C] font-bold">B</span>
                            </li>
                            {allVals.slice(0, 8).map((v) => (
                              <li key={v} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center">
                                <span className="truncate text-[#1E3A5F]">{v}</span>
                                <span className="text-[#3A8FA5] font-medium min-w-[24px] text-right">{mapA.get(v) || 0}</span>
                                <span className="text-[#B9502C] font-medium min-w-[24px] text-right">{mapB.get(v) || 0}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            }
            const s = displayStats[f.name] || { count: 0 };
            return (
              <div key={f.name} className="border border-[#E1E9F0] rounded-md p-4 bg-[#F4F7FB]/40">
                <div className="text-xs tracking-widest uppercase text-[#5F758F]">{f.type}</div>
                <div className="font-medium text-[#1E3A5F] mb-2">{f.name}</div>
                {s.count === 0 && <div className="text-sm text-[#5F758F] italic">Aucune valeur extraite</div>}
                {s.type === "number" && s.count > 0 && (
                  <>
                    <div className="text-sm text-[#1E3A5F] space-y-0.5 mb-3">
                      <div><span className="text-[#5F758F]">n :</span> {s.count} · <span className="text-[#5F758F]">moyenne :</span> {s.mean} · <span className="text-[#5F758F]">médiane :</span> {s.median}</div>
                      <div><span className="text-[#5F758F]">min – max :</span> {s.min} – {s.max}</div>
                    </div>
                    {s.histogram && s.histogram.length > 0 && (
                      <div style={{ width: "100%", height: 180 }}>
                        <ResponsiveContainer>
                          <BarChart data={s.histogram} margin={{ top: 4, right: 8, left: -20, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E1E9F0" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#5F758F" }} interval={0} angle={-25} textAnchor="end" height={50} />
                            <YAxis tick={{ fontSize: 10, fill: "#5F758F" }} allowDecimals={false} />
                            <Tooltip
                              contentStyle={{ background: "#fff", border: "1px solid #E1E9F0", borderRadius: 6, fontSize: 12 }}
                              formatter={(v) => [`${v} CR`, "Effectif"]}
                              labelFormatter={(l) => `Intervalle ${l}`}
                            />
                            <Bar dataKey="count" fill="#3A8FA5" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </>
                )}
                {s.type !== "number" && s.count > 0 && s.top && (
                  <ul className="text-sm text-[#1E3A5F] space-y-0.5">
                    {s.top.map((t) => (
                      <li key={t.value} className="flex justify-between gap-2">
                        <span className="truncate">{t.value}</span>
                        <span className="text-[#5F758F]">{t.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl">Lignes extraites {activeFilterCount > 0 && <span className="text-sm font-normal text-[#3A8FA5]">({rowsToShow.length} filtrées)</span>}</h3>
          <Button size="sm" variant="outline" onClick={() => onExport(rowsToShow, fieldNames)} className="rounded-md" data-testid="doc-export-filtered">
            <DownloadSimple size={14} className="mr-1.5" /> CSV {activeFilterCount > 0 ? "(filtré)" : ""}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs tracking-widest uppercase text-[#5F758F] border-b border-[#E1E9F0]">
                <th className="py-2 pr-3">Fichier</th>
                {fieldNames.map((f) => <th key={f} className="py-2 pr-3">{f}</th>)}
              </tr>
            </thead>
            <tbody>
              {rowsToShow.map((r, i) => (
                <tr key={`${r.filename}-${i}`} className="border-b border-[#E1E9F0]">
                  <td className="py-2 pr-3 text-[#1E3A5F] font-medium truncate max-w-xs">{r.filename}</td>
                  {r.error ? (
                    <td colSpan={fieldNames.length} className="py-2 pr-3 text-[#B9502C] text-xs">
                      <Warning size={12} className="inline mr-1" />{r.error}
                    </td>
                  ) : (
                    fieldNames.map((f) => (
                      <td key={f} className="py-2 pr-3 text-[#1E3A5F]">
                        {r.extracted?.[f] == null ? <span className="text-[#5F758F] italic">—</span> : String(r.extracted[f])}
                      </td>
                    ))
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
